import React, { useState, useMemo } from 'react';
import type { DiagramDetailRow } from '../../types/dashboard';

interface Props {
  data: DiagramDetailRow[] | null;
}

type SortKey = 'diagram_name' | 'user' | 'status' | 'items_marked' | 'last_updated';
type SortDir = 'asc' | 'desc';

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  'Not Started': { bg: '#f3f4f6', color: '#6b7280' },
  'In Progress': { bg: '#fef3c7', color: '#92400e' },
  'QC': { bg: '#d1fae5', color: '#065f46' },
};

export default function DiagramDetailTable({ data }: Props) {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('last_updated');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.toLowerCase();
    let rows = q ? data.filter((r) => (r.diagram_name || '').toLowerCase().includes(q) || r.user.toLowerCase().includes(q)) : data;
    rows = [...rows].sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const cmp = String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [data, search, sortKey, sortDir]);

  if (!data || data.length === 0) {
    return (
      <div style={{ ...sectionStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120, color: '#9ca3af' }}>
        No diagram data available
      </div>
    );
  }

  const arrow = sortDir === 'asc' ? ' ▲' : ' ▼';

  return (
    <div style={sectionStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 style={headingStyle}>Per-Diagram Detail</h3>
        <input
          type="text"
          placeholder="Search diagrams..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={searchStyle}
        />
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort(col.key as SortKey)}
                >
                  {col.label}{sortKey === col.key ? arrow : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => {
              const sc = STATUS_COLORS[row.status] || STATUS_COLORS['Not Started'];
              return (
                <tr key={row.diagram_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={tdStyle}>{row.diagram_name || row.diagram_id}</td>
                  <td style={tdStyle}>{row.user}</td>
                  <td style={tdStyle}>
                    <span style={{ ...badgeStyle, backgroundColor: sc.bg, color: sc.color }}>{row.status}</span>
                  </td>
                  <td style={tdNumStyle}>{row.items_marked}</td>
                  <td style={tdStyle}>{formatDate(row.last_updated)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: 11, color: '#9ca3af', margin: '12px 0 0', textAlign: 'right' }}>
        Showing {filtered.length} of {data.length} diagrams
      </p>
    </div>
  );
}

function formatDate(iso: string): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

const COLUMNS = [
  { key: 'diagram_name', label: 'Diagram' },
  { key: 'user', label: 'User' },
  { key: 'status', label: 'Status' },
  { key: 'items_marked', label: 'Items' },
  { key: 'last_updated', label: 'Last Updated' },
];

const sectionStyle: React.CSSProperties = {
  backgroundColor: '#fff',
  borderRadius: 12,
  padding: 24,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
};

const headingStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  color: '#111827',
  margin: 0,
};

const searchStyle: React.CSSProperties = {
  padding: '6px 12px',
  fontSize: 13,
  borderRadius: 6,
  border: '1px solid #e5e7eb',
  outline: 'none',
  width: 200,
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  fontSize: 11,
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderBottom: '2px solid #e5e7eb',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  color: '#374151',
};

const tdNumStyle: React.CSSProperties = {
  ...tdStyle,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};

const badgeStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 4,
  fontSize: 11,
  fontWeight: 600,
};
